import React, { createContext, useContext, useState, useEffect, useMemo, useRef, ReactNode } from 'react';
import { 
  processQueue, 
  loadQueue, 
  addQueueItem, 
  removeQueueItem,
  QueueItem
} from '../services/queueService';
import { useAuth } from './AuthContext';

// Queue-Kontext-Typen
type SyncState = 'idle' | 'syncing' | 'completed' | 'failed';

interface QueueContextType {
  items: QueueItem[];
  addToQueue: (type: string, data: any) => void;
  removeFromQueue: (id: string) => void;
  isProcessing: boolean;
  isOnline: boolean;
  syncQueue: () => Promise<void>;
  syncStatus: {
    pending: number;
    lastSync: Date | null;
    error: string | null;
    state: SyncState;
    lastResult?: { successful: number; failed: number };
  };
}

const QueueContext = createContext<QueueContextType>({
  items: [],
  addToQueue: () => {},
  removeFromQueue: () => {},
  isProcessing: false,
  isOnline: true,
  syncQueue: async () => {},
  syncStatus: {
    pending: 0,
    lastSync: null,
    error: null,
    state: 'idle'
  }
});

export const QueueProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<QueueItem[]>([]);
  const isInitializedRef = useRef<boolean>(false); // ✅ useRef Guard statt useState (überlebt StrictMode)
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [syncStartTime, setSyncStartTime] = useState<number | null>(null);
  const [lastResult, setLastResult] = useState<{ successful: number; failed: number } | undefined>(undefined);
  const { isAuthenticated } = useAuth();

  // Auto-Recovery nach 60s wenn State auf 'syncing' hängen bleibt
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (syncState === 'syncing' && syncStartTime) {
      const timeoutId = setTimeout(() => {
        const elapsed = Date.now() - syncStartTime;
        if (elapsed >= 60000) { // 60 Sekunden
          console.error('Sync timeout - auto-recovery nach 60s');
          setSyncState('failed');
          setSyncError('⚠ Sync-Timeout – Bitte erneut versuchen');
          setIsProcessing(false);
          setSyncStartTime(null);
        }
      }, 60000);

      return () => clearTimeout(timeoutId);
    }
  }, [syncState, syncStartTime]);

  // Cleanup bei Component-Unmount
  useEffect(() => {
    return () => {
      if (syncState === 'syncing') {
        console.log('QueueContext unmount - reset sync state');
        setSyncState('idle');
        setIsProcessing(false);
      }
    };
  }, [syncState]);

  // Queue aus IndexedDB laden (OHNE State-Update - nur Return)
  const loadQueueFromIndexedDB = async () => {
    try {
      const loadedItems = await loadQueue();
      // ❌ NICHT setItems(loadedItems) hier - das verursacht Badge-Flackern!
      // State-Update muss NACH Processing passieren
      return loadedItems;
    } catch (error) {
      console.error('Fehler beim Laden der Queue:', error);
      return [];
    }
  };

  // Online/Offline-Status überwachen
  useEffect(() => {
    const handleOnlineStatus = async () => {
      setIsOnline(true);
      
      // ✅ FIX 8: Auth-Check ENTFERNT (gleicher Grund wie bei initializeQueue)
      // Queue-Items sollten verarbeitet werden sobald online, unabhängig von Auth-Status
      if (isInitializedRef.current) {
        const loadedItems = await loadQueueFromIndexedDB();
        
        if (loadedItems.length > 0) {
          console.log('[ONLINE] Processing queue items before state update...');
          
          // ✅ FIX 7: State sofort auf [] setzen
          setItems([]);
          
          try {
            const { successful, failed } = await processQueue(loadedItems);
            
            // Nach Processing: Queue neu laden
            const finalQueue = await loadQueue();
            setItems(finalQueue);
            
            if (failed.length > 0) {
              setSyncState('failed');
            } else if (successful.length > 0) {
              setSyncState('completed');
              setTimeout(() => setSyncState('idle'), 3000);
            }
          } catch (error) {
            console.error('[ONLINE] Processing error:', error);
            setItems(loadedItems); // Fallback
          }
        }
      }
    };

    const handleOfflineStatus = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOfflineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOfflineStatus);
    };
  }, [isAuthenticated]);

  // Queue beim Start laden - NUR EINMAL beim Mount
  // ✅ FIX: Await processQueue BEFORE setting items (verhindert Race-Condition bei Page-Reload)
  // ✅ FIX: useRef-Guard verhindert doppelte Initialisierung (React StrictMode remountet 2x)
  // ✅ FIX 7: SOFORTIGES State-Reset auf [] während Processing (Badge zeigt NIE alte Items)
  // Warum: Badge soll NIE den Zwischenstand sehen (weder 0→1 noch 1→0 Flackern)
  useEffect(() => {
    // ✅ Guard: Verhindert mehrfache Initialisierung bei StrictMode/Remounts
    if (isInitializedRef.current) {
      console.log('[QUEUE-INIT] Already initialized, skipping');
      return;
    }
    
    const initializeQueue = async () => {
      console.log('[QUEUE-INIT] Starting initialization...');
      
      try {
        const loadedItems = await loadQueue();
        console.log('[QUEUE-INIT] Loaded items:', loadedItems.length);
        console.log('[QUEUE-INIT] Auth status:', { isAuthenticated, isOnline });
        
        // ✅ FIX 8: Processing NUR wenn online (Auth-Check ENTFERNT!)
        // Warum entfernt: Bei Page-Load ist isAuthenticated oft noch FALSE
        // (Auth-Context mountet parallel). Items sollten trotzdem verarbeitet werden.
        // API-Calls sind bereits auth-protected, kein Security-Risk.
        if (loadedItems.length > 0 && isOnline) {
          console.log('[QUEUE-INIT] Processing items before state update...');
          
          // ✅ FIX 7: SOFORT State auf [] setzen - Badge zeigt 0 während Processing
          // Warum: Badge rendert BEVOR processQueue() finished ist.
          // Items werden sowieso gleich aus IndexedDB gelöscht (nach successful processing)
          setItems([]);
          
          const { successful, failed } = await processQueue(loadedItems);
          console.log('[QUEUE-INIT] Processing done. Successful:', successful.length, 'Failed:', failed.length);
          
          // Nach Processing: Queue neu laden (sollte leer sein oder nur failed items)
          const finalQueue = await loadQueue();
          console.log('[QUEUE-INIT] Final queue length:', finalQueue.length);
          
          setItems(finalQueue);
          
          // Sync-Status updaten
          if (failed.length > 0) {
            setSyncState('failed');
            setSyncError(`${failed.length} von ${successful.length + failed.length} Uploads fehlgeschlagen`);
          } else if (successful.length > 0) {
            setSyncState('completed');
            setTimeout(() => setSyncState('idle'), 3000);
          }
        } else {
          // Keine Items ODER offline → direkt setzen
          console.log('[QUEUE-INIT] No processing needed, setting items directly');
          console.log('[QUEUE-INIT] Reason:', loadedItems.length === 0 ? 'no items' : 'offline');
          setItems(loadedItems);
        }
        
        isInitializedRef.current = true;
        console.log('[QUEUE-INIT] Initialization complete');
      } catch (error) {
        console.error('[QUEUE-INIT] Error during initialization:', error);
        isInitializedRef.current = true; // Auch bei Error als initialized markieren
      }
    };
    
    initializeQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← LEER! Nur beim Mount, nicht bei Auth/Online-Changes

  // ServiceWorker-Updates überwachen
  useEffect(() => {
    const handleServiceWorkerMessage = async (event: MessageEvent) => {
      const data = event.data;
      
      // ✅ FIX: Bei erfolgreicher Synchronisierung Queue laden OHNE State-Update
      // ServiceWorker hat bereits synchronisiert, wir laden nur finalen State
      if (data.type === 'SYNC_COMPLETED') {
        const finalQueue = await loadQueue();
        setItems(finalQueue);
        setLastSync(new Date());
      }
    };
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }
    
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  // Queue-Verarbeitung mit State Machine + Timeout-Tracking
  const processQueueItems = async (queueItems?: QueueItem[]) => {
    const itemsToProcess = queueItems || items;
    
    if (isProcessing || !isOnline || !isAuthenticated || itemsToProcess.length === 0) {
      return;
    }

    setIsProcessing(true);
    setSyncState('syncing');
    setSyncStartTime(Date.now()); // Zeitstempel für 60s Timeout

    try {
      const { successful, failed } = await processQueue(itemsToProcess);

      // Ergebnis speichern für UI-Feedback
      setLastResult({ 
        successful: successful.length, 
        failed: failed.length 
      });

      // ✅ FIX: Vereinfachte setState-Strategie (nur DB-Reload, kein Optimistic Update)
      // Warum: Doppelte setState-Aufrufe über await-Boundary erzeugen zwei separate
      // Render-Batches (React 18). Badge-Component kann Zwischenstand sehen.
      // Bei 500ms Mock-Upload-Delay bringt Optimistic Update keinen UX-Gewinn.
      const updatedQueue = await loadQueue();
      setItems(updatedQueue);
      
      setLastSync(new Date());
      setSyncStartTime(null); // Reset nach erfolgreichem Sync
      
      // State je nach Ergebnis setzen
      if (failed.length > 0) {
        setSyncState('failed');
        setSyncError(`${failed.length} von ${successful.length + failed.length} Uploads fehlgeschlagen`);
        setSyncStartTime(null);
      } else {
        setSyncState('completed');
        setSyncError(null);
        setSyncStartTime(null);
        
        // Nach 3 Sekunden zurück zu 'idle'
        setTimeout(() => setSyncState('idle'), 3000);
      }
    } catch (error) {
      console.error('Fehler bei der Queue-Verarbeitung:', error);
      setSyncState('failed');
      setSyncError('Synchronisierungsfehler');
      setSyncStartTime(null);
    } finally {
      setIsProcessing(false);
    }
  };

  // Manueller Sync der Queue (für UI-Aktionen)
  const syncQueue = async () => {
    if (isProcessing || !isOnline) return;
    const loadedItems = await loadQueue();
    if (loadedItems.length === 0) return;

    // Retries für blockierte Items zurücksetzen, damit der User sie explizit nochmal versuchen kann
    const itemsToSync = await Promise.all(
      loadedItems.map(async (item) => {
        if (item.retries >= 5) {
          const reset = { ...item, retries: 0 };
          await addQueueItem(reset);
          return reset;
        }
        return item;
      })
    );

    setItems(itemsToSync);
    setIsProcessing(true);
    setSyncState('syncing');
    setSyncStartTime(Date.now());
    try {
      const { successful, failed } = await processQueue(itemsToSync);
      setLastResult({ successful: successful.length, failed: failed.length });
      const updatedQueue = await loadQueue();
      setItems(updatedQueue);
      setLastSync(new Date());
      setSyncStartTime(null);
      if (failed.length > 0) {
        setSyncState('failed');
        setSyncError(`${failed.length} von ${successful.length + failed.length} Uploads fehlgeschlagen`);
      } else {
        setSyncState('completed');
        setSyncError(null);
        setTimeout(() => setSyncState('idle'), 3000);
      }
    } catch (err) {
      setSyncState('failed');
      setSyncError('Sync fehlgeschlagen');
    } finally {
      setIsProcessing(false);
      setSyncStartTime(null);
    }
  };

  // Element zur Queue hinzufügen
  const addToQueue = async (type: string, data: any) => {
    const queueKey =
      data && typeof data === 'object' && typeof data.queueKey === 'string' && data.queueKey.trim() !== ''
        ? data.queueKey.trim()
        : null;
    const newItem: QueueItem = {
      id: queueKey || Date.now().toString(),
      type,
      data,
      retries: 0,
      createdAt: new Date()
    };

    // In IndexedDB speichern
    await addQueueItem(newItem);
    
    // ❌ ENTFERNT: Optimistisches State-Update verursacht Badge-Flackern
    // setItems(prevItems => [...prevItems, newItem]);
    // Warum entfernt: Bei schnellem Upload sieht Badge "1" zwischen optimistic
    // update und finalem DB-Reload. processQueueItems macht bereits finales
    // setItems(updatedQueue), deshalb ist optimistic update überflüssig.

    // Wenn online und authentifiziert, sofort verarbeiten
    // ✅ FIX: processQueueItems macht finales setItems(updatedQueue)
    if (isOnline && isAuthenticated && !isProcessing) {
      await processQueueItems([newItem]);
    } else {
      // Nur offline Background Sync registrieren (nicht wenn sofort verarbeitet wird)
      if ('serviceWorker' in navigator && 'SyncManager' in window) {
        try {
          const registration = await navigator.serviceWorker.ready;
          if ('sync' in registration) {
            const syncTag = type === 'image_upload' ? 'imageUploads' : 'checklistUpdates';
            await (registration as any).sync.register(syncTag);
          }
        } catch (error) {
          console.warn('Background Sync nicht verfügbar:', error);
        }
      }
      // ✅ Wenn offline oder processing läuft: Manuell State updaten
      const updatedQueue = await loadQueue();
      setItems(updatedQueue);
    }
  };

  // Element aus der Queue entfernen
  const removeFromQueue = async (id: string) => {
    // Aus IndexedDB entfernen
    await removeQueueItem(id);
    
    // State aktualisieren
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  };

  // ✅ FIX: Context-Value mit useMemo wrappen (verhindert neue Objekt-Referenz bei jedem Render)
  // Warum: React Context nutzt Object.is() für Vergleiche. Ohne useMemo wird bei jedem
  // Provider-Render ein neues Objekt erstellt → alle Consumer re-rendern, auch wenn
  // items.length identisch ist. Bei schnellen State-Updates kann Badge zwischen zwei
  // Batches evaluiert werden und sieht temporär inkonsistente Werte.
  const contextValue: QueueContextType = useMemo(() => ({
    items,
    addToQueue,
    removeFromQueue,
    isProcessing,
    isOnline,
    syncQueue,
    syncStatus: {
      pending: items.length,
      lastSync,
      error: syncError,
      state: syncState,
      lastResult
    }
  }), [items, addToQueue, removeFromQueue, isProcessing, isOnline, syncQueue, lastSync, syncError, syncState, lastResult]);

  return (
    <QueueContext.Provider value={contextValue}>
      {children}
    </QueueContext.Provider>
  );
};

// Hook für einfachen Zugriff
export const useQueue = () => useContext(QueueContext);
